import { forwardRef, HTMLAttributes, useMemo } from 'react'

import type { TrackReferenceOrPlaceholder } from '@livekit/components-core'

import { VoiceOff } from '@/src/assets/icons'
import { useMicrophoneBlocked } from '@/src/hooks/useMicrophoneBlocked'
import { mergeProps } from '@/src/utils'
import { useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'

/** @public */
export interface MicrophoneBlockedIndicatorProps extends HTMLAttributes<HTMLDivElement> {
  trackRef: TrackReferenceOrPlaceholder
}

/**
 * The `MicrophoneBlockedIndicator` shows whether the participant's microphone is blocked by admin.
 *
 * @example
 * ```tsx
 * <MicrophoneBlockedIndicator trackRef={trackRef} />
 * ```
 * @public
 */
export const MicrophoneBlockedIndicator = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  MicrophoneBlockedIndicatorProps
>(function MicrophoneBlockedIndicator({ trackRef, ...props }: MicrophoneBlockedIndicatorProps, ref) {
  const isMicrophoneBlocked = useMicrophoneBlocked(trackRef)
  const { text_primary_color } = useAppSelector(selectBranding)

  const htmlProps = useMemo(
    () =>
      mergeProps(props, {
        className: 'lk-microphone-blocked-indicator',
        style: {
          ...props?.style,
          fill: text_primary_color ?? '#fff',
          opacity: 0.5,
          marginRight: '0.25rem',
          height: '16px',
          marginTop: '-3px',
        },
      }),
    [props, text_primary_color],
  )

  if (!isMicrophoneBlocked) {
    return null
  }

  return (
    <div ref={ref} {...htmlProps} data-lk-microphone-blocked={true}>
      {props.children ?? <VoiceOff width={20} height={20} fill={text_primary_color ?? '#ffffff'} />}
    </div>
  )
})

